// pages/PromoCodes.tsx
import React from "react";
import { FaInfo } from "react-icons/fa";
import Button from "../components/Button";
import ConfirmationDialog from "../components/ConfirmationDialog";
import DataTableView from "../components/DataTableView2";
import DatePicker from "../components/DatePicker";
import AddPromoCodeModal, { serviceTypes } from "../components/promoCode/AddPromoCodeModal";
import EditPromoCode from "../components/promoCode/EditPromoCode";
import ViewPromoCode from "../components/promoCode/ViewPromoCode";
import Select from "../components/Select";
import Toggle from "../components/Toggle";
import { capitalize } from "../utils/helpers";
import { IPromoCodeType } from "../utils/types";
import usePromoCodes from '../hooks/logics/management/use-promo-codes-dashboard';

const serviceMap = {
  package_pickup_meta: "Package Pickup",
  curbside_pickup_meta: "Curbside Pickup",
  instore_pickup_meta: "Instore Pickup",
};

export const formatServiceTypes = (
  services: ["package_pickup_meta" | "curbside_pickup_meta" | "instore_pickup_meta"]
) => services.map((service) => serviceMap[service]).join(", ");

const PromoCodes = () => {
  const {
    promoCodes, totalPages, isLoading, isDeletingPromoCode,
    currentPage, setCurrentPage,
    fromDate, toDate,
    serviceType, setServiceType,
    isActive, setIsActive,
    selectedPromo, setSelectedPromo,
    isAddModalVisible, setIsAddModalVisible,
    isConfirmationModalOpen, setIsConfirmationModalOpen,
    isEditModalOpen, setIsEditModalOpen,
    isViewPromoCodeVisible, setIsViewPromoCodeVisible,
    promoCodeStatusChangeId,
    handleSearch, clearFilters,
    handleDeletePromo, handlePromoCodeActivationChange,
    handleFromDateChange, handleToDateChange,
    isApplyButtonDisabled, isClearButtonDisabled,
    refetch,
  } = usePromoCodes();

  const columns = [
    {
      header: "Promo Code",
      className: "md:p-0 md:pl-10",
      accessor: (item: IPromoCodeType) => (
        <div className="md:!pl-7 md:w-[300px]">{item.promoCode || "N/A"}</div>
      ),
    },
    {
      header: "Discount Type",
      accessor: (item: IPromoCodeType) => (
        <div className="md:w-[150px]">{capitalize(item.discountType)}</div>
      ),
    },
    {
      header: "Discount Value",
      accessor: (item: IPromoCodeType) => (
        <div className="md:w-[150px]">
          {item.discountType === "flat"
            ? `$ ${item.discountValue.toFixed(2)}`
            : `${item.discountValue}%`}
        </div>
      ),
    },
    {
      header: "Service Type",
      headerClassName: "whitespace-nowrap",
      accessor: (item: IPromoCodeType) => (
        <div className="ml-3 md:ml-0 md:w-[350px]">{formatServiceTypes(item.serviceType)}</div>
      ),
    },
    {
      header: "Current Usage",
      accessor: (item: IPromoCodeType) => (
        <div className="md:w-[150px]">{item.currentUsage}</div>
      ),
    },
    {
      header: "Actions",
      accessor: (item: IPromoCodeType) => (
        <div className="flex flex-row items-center space-x-2 md:w-[200px]">
          {item.isPromoCodeActive !== undefined && item.isPromoCodeActive !== null && (
            <div className="ml-2">
              <Toggle
                labelClassName="mr-2"
                label="Active"
                disabled={promoCodeStatusChangeId === item._id}
                checked={item.isPromoCodeActive === true}
                onChange={() => handlePromoCodeActivationChange(item._id, item.isPromoCodeActive!)}
              />
            </div>
          )}
          <Button
            onClick={() => { setSelectedPromo(item); setIsEditModalOpen(true); }}
            variant="ghost"
            className="text-primary w-max p-0"
          >
            Edit
          </Button>
          <Button
            onClick={() => { setSelectedPromo(item); setIsConfirmationModalOpen(true); }}
            variant="ghost"
            className="text-red-600 w-max p-0"
          >
            Delete
          </Button>
        </div>
      ),
    },
    {
      header: "Info",
      accessor: (item: IPromoCodeType) => (
        <div className="mt-0.5 md:mt-0 md:pr-10">
          <Button
            className="md:p-2 p-1 rounded-full"
            onClick={() => { setSelectedPromo(item); setIsViewPromoCodeVisible(true); }}
          >
            <FaInfo className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full mb-4 flex items-center justify-end gap-4 md:mt-0">
        <Button onClick={() => setIsAddModalVisible(true)} className="w-full min-h-0 h-10 md:h-12 md:w-auto md:px-3">
          Add promo code
        </Button>
      </div>

      <div className="md:pb-4 w-full flex flex-wrap items-center justify-end gap-4">
        <div className="w-full flex flex-row gap-x-2 md:gap-x-4 md:w-max mt-4 md:mt-0">
          <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
            <label className="mr-2">From:</label>
            <DatePicker
              selected={fromDate ? new Date(fromDate) : null}
              onChange={handleFromDateChange}
              placeholderText="MM/DD/YYYY"
              className="min-h-0 h-8 md:h-10 py-1"
              inputClassName="py-0 md:py-2 h-8 md:h-10"
              popperClassName="!left-7"
            />
          </div>
          <div className="flex flex-col items-start md:flex-row md:items-center gap-y-1 w-1/2 md:w-full">
            <label className="mr-2">To:</label>
            <DatePicker
              selected={toDate ? new Date(toDate) : null}
              onChange={handleToDateChange}
              placeholderText="MM/DD/YYYY"
              className="min-h-0 h-8 md:h-10 py-1"
              inputClassName="py-0 md:py-2 h-8 md:h-10"
              popperClassName="!-left-7"
            />
          </div>
        </div>

        <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4">
          <Select
            options={[{ label: "All", value: "all" }, ...serviceTypes]}
            placeholder="Service type"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
            containerClassName="w-1/2 md:w-auto"
          />
          <Select
            options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
            placeholder="Active"
            value={isActive}
            onChange={(e) => setIsActive(e.target.value)}
            className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
            containerClassName="w-1/2 md:w-auto"
          />
        </div>

        <div className="w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0">
          <Button disabled={isApplyButtonDisabled} onClick={handleSearch} className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10">
            Apply
          </Button>
          <Button disabled={isClearButtonDisabled} onClick={clearFilters} className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10">
            Clear
          </Button>
        </div>
      </div>

      <DataTableView
        columns={columns}
        isFetching={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        emptyStateMessage="No promo codes found"
        data={promoCodes}
      />

      <AddPromoCodeModal
        isVisible={isAddModalVisible}
        onHide={() => setIsAddModalVisible(false)}
        onSuccess={refetch}
      />

      <ConfirmationDialog
        text="Are you sure you want to delete this promo?"
        handleClick={handleDeletePromo}
        isVisible={isConfirmationModalOpen}
        onHide={() => setIsConfirmationModalOpen(false)}
        isSubmitButtonDisabled={isDeletingPromoCode}
        isSubmitting={isDeletingPromoCode}
      />

      <EditPromoCode
        isVisible={isEditModalOpen}
        onHide={() => setIsEditModalOpen(false)}
        promoCode={selectedPromo}
        onSuccess={refetch}
      />

      <ViewPromoCode
        isVisible={isViewPromoCodeVisible}
        onHide={() => { setSelectedPromo(null); setIsViewPromoCodeVisible(false); }}
        promoCode={selectedPromo}
      />
    </>
  );
};

export default PromoCodes;